/**
 * Equity Curve 시각화 카드
 */
import { memo } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { EquityPoint } from '../../types/performance';
import {
  formatDateTick,
  formatValueTick,
  formatTooltipLabel,
  currencyFormatter,
} from '../../utils/formatters';

interface EquityCurveChartProps {
  data: EquityPoint[];
}

const EquityCurveChart = memo(function EquityCurveChart({ data }: EquityCurveChartProps) {
  const hasData = data && data.length > 0;
  const latestValue = hasData ? data[data.length - 1].value : null;
  const baseline = hasData ? data[0].value : null;

  return (
    <section className="performance-chart-shell">
      <header className="performance-chart-header">
        <div>
          <h3>Equity Curve</h3>
          <p>
            누적 손익 흐름을 기준값과 비교합니다.
          </p>
        </div>
        {latestValue !== null && (
          <span className="performance-chart-meta">
            현재 잔고 {currencyFormatter.format(latestValue)}
          </span>
        )}
      </header>
      {hasData ? (
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 16, right: 24, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#223548" />
            <XAxis
              dataKey="date"
              tickFormatter={formatDateTick}
              minTickGap={24}
              stroke="#7890a8"
            />
            <YAxis
              tickFormatter={formatValueTick}
              width={80}
              stroke="#7890a8"
            />
            {baseline !== null && (
              <ReferenceLine
                y={baseline}
                stroke="#7890a8"
                strokeDasharray="4 4"
                label={{
                  value: '초기 자본',
                  fill: '#9fb1c4',
                  position: 'insideTopRight',
                  fontSize: 11,
                }}
              />
            )}
            <Tooltip
              formatter={(value: number) => currencyFormatter.format(value)}
              labelFormatter={formatTooltipLabel}
              contentStyle={{
                background: '#071018',
                borderColor: '#223548',
                borderRadius: 8,
                color: '#dbe7f3',
              }}
              labelStyle={{ color: '#dbe7f3' }}
              itemStyle={{ color: '#dbe7f3' }}
            />
            <Line
              type="monotone"
              dataKey="value"
              stroke="#38bdf8"
              strokeWidth={2.5}
              dot={false}
              activeDot={{ r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <EmptyState />
      )}
    </section>
  );
});

export default EquityCurveChart;

function EmptyState() {
  return (
    <div className="performance-chart-empty">
      데이터가 없습니다.
    </div>
  );
}
